import React, { useState, useEffect } from 'react';
import { X, MapPin, AlertCircle, Save, RotateCw, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

const GeocodingResolutionModal = ({ isOpen, onClose, failures = [], routeId, onSuccess }) => {
    const [orders, setOrders] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ address: '', city: '' });
    const [processing, setProcessing] = useState(false);
    const [retryStatus, setRetryStatus] = useState({}); // { [orderId]: 'pending' | 'success' | 'error' }

    useEffect(() => {
        console.log("GeocodingResolutionModal loaded - Google Maps Version");
        if (isOpen && failures.length > 0) {
            setOrders(failures);
            setRetryStatus({});
        }
    }, [isOpen, failures]);

    if (!isOpen) return null;

    const handleEdit = (order) => {
        setEditingId(order.orderId);
        setEditForm({
            address: order.address || '',
            city: 'Bogotá' // Default fallback, could be enhanced
        });
    };

    const handleAddressSelect = (selection) => {
        console.log("Selected Address:", selection);
        setEditForm({
            address: selection.address, // Full address from Mapbox
            city: 'Bogotá' // We restrict to Bogota anyway, but could extract from context if needed
        });
    };

    const handleSaveAddress = async (orderId) => {
        try {
            setProcessing(true);
            await api.put(`/orders/${orderId}`, {
                customer_address: editForm.address,
                customer_city: editForm.city
            });

            setOrders(prev => prev.map(o =>
                o.orderId === orderId
                    ? { ...o, address: editForm.address }
                    : o
            ));

            setEditingId(null);
            toast.success('Dirección actualizada');

            // Auto-retry specific order geocoding? 
            // The backend endpoint is bulk route geocode, but we can trigger it and it will only process missing ones.
            // Or we can just mark it as "ready to retry" visually.
        } catch (error) {
            console.error('Error saving address:', error);
            toast.error('Error al guardar dirección');
        } finally {
            setProcessing(false);
        }
    };

    const handleRetryGeocoding = async () => {
        try {
            setProcessing(true);
            const response = await api.post(`/routes/${routeId}/geocode`);

            if (response.data.success) {
                const results = response.data.results || [];
                const newStatus = {};
                let solvedCount = 0;

                results.forEach(res => {
                    if (res.success) {
                        newStatus[res.orderId] = 'success';
                        solvedCount++;
                    } else {
                        newStatus[res.orderId] = 'error';
                    }
                });

                setRetryStatus(newStatus);

                if (solvedCount > 0) {
                    toast.success(`${solvedCount} pedidos geocodificados correctamente`);
                    if (onSuccess) onSuccess(); // Reload map
                } else {
                    toast.error('No se pudieron geocodificar los pedidos. Verifica las direcciones.');
                }
            }
        } catch (error) {
            console.error('Error identifying geocoding retry:', error);
            toast.error('Error al reintentar geocodificación');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-red-50">
                    <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <h2 className="font-bold text-lg">Pedidos sin Ubicación ({orders.length})</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-4 bg-blue-50 text-sm text-blue-800 border-b border-blue-100">
                    <p>Estos pedidos no se pudieron localizar automáticamente. Usa el buscador para encontrar la dirección correcta en Bogotá.</p>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '500px' }}>
                    {orders.map(order => (
                        <div
                            key={order.orderId}
                            className={`border rounded-lg p-3 ${retryStatus[order.orderId] === 'success' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                                }`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <span className="font-bold text-sm text-gray-900">#{order.orderNumber}</span>
                                    <span className="mx-2 text-gray-400">|</span>
                                    <span className="text-sm text-gray-700">{order.customerName}</span>
                                </div>
                                {retryStatus[order.orderId] === 'success' && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> Solucionado
                                    </span>
                                )}
                            </div>

                            {editingId === order.orderId ? (
                                <div className="space-y-2 mt-2">
                                    <div className="relative z-50 h-12">
                                        <label className="block text-xs font-bold text-gray-700 mb-1">
                                            Buscar Dirección (Bogotá)
                                        </label>
                                        <GooglePlacesAutocomplete
                                            onSelect={handleAddressSelect}
                                            placeholder="Escribe la dirección (ej: Calle 85...)"
                                        />
                                        <div className="text-[10px] text-gray-400 mt-1 text-right">Powered by Google Maps</div>
                                    </div>

                                    <div className="flex justify-end gap-2 pt-8">
                                        <button
                                            onClick={() => setEditingId(null)}
                                            className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={() => handleSaveAddress(order.orderId)}
                                            disabled={processing || !editForm.address}
                                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1 shadow-sm transition-colors disabled:opacity-50"
                                        >
                                            <Save className="w-3 h-3" /> Guardar Dirección
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-between items-end">
                                    <div className="text-sm text-gray-600 flex items-start gap-1.5 mt-1">
                                        <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                                        <span className={`${order.address ? '' : 'text-red-400 italic'}`}>
                                            {order.address || 'Sin dirección'}
                                        </span>
                                    </div>

                                    {retryStatus[order.orderId] !== 'success' && (
                                        <button
                                            onClick={() => handleEdit(order)}
                                            className="text-xs text-blue-600 font-medium hover:text-blue-800 underline ml-4 whitespace-nowrap"
                                        >
                                            Editar Dirección
                                        </button>
                                    )}
                                </div>
                            )}

                            {order.reason && !editingId && retryStatus[order.orderId] !== 'success' && (
                                <p className="text-xs text-red-500 mt-2 ml-5">
                                    Error: {order.reason}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                        Total fallidos: {orders.length}
                    </span>
                    <button
                        onClick={handleRetryGeocoding}
                        disabled={processing}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-semibold shadow-sm transition-all"
                    >
                        <RotateCw className={`w-4 h-4 ${processing ? 'animate-spin' : ''}`} />
                        {processing ? 'Procesando...' : 'Reintentar Geocodificación'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GeocodingResolutionModal;
